import type { ChatDeltaDto, ChatMessageDto } from "@/lib/chat";

export interface SSEControlFrame {
	control: "streamEnd" | "reconnect";
}

/** 生成中の断片を運ぶフレーム。`event: delta` の直後の `data:` 行から作る。 */
export interface SSEDeltaFrame {
	delta: ChatDeltaDto;
}

export type SSEFrame = ChatMessageDto | SSEControlFrame | SSEDeltaFrame;

export function isControlFrame(frame: SSEFrame): frame is SSEControlFrame {
	return "control" in frame;
}

export function isStreamEnd(frame: SSEFrame): frame is SSEControlFrame {
	return isControlFrame(frame) && frame.control === "streamEnd";
}

export function isReconnect(frame: SSEFrame): frame is SSEControlFrame {
	return isControlFrame(frame) && frame.control === "reconnect";
}

export function isDeltaFrame(frame: SSEFrame): frame is SSEDeltaFrame {
	return "delta" in frame;
}

/**
 * SSE の1行をチャットメッセージ・生成中断片・制御フレームとして解釈する。
 *
 * バックエンドは通常メッセージを `data: {ChatMessageDto}`、生成中断片を
 * `event: delta` + `data: {ChatDeltaDto}`、意図的なアイドル終了を `event: streamEnd`、
 * backend 停止に伴う再接続要求を `event: reconnect` として送る。
 * `event:` 行自体は値を持たないため、直前に読んだイベント名を `eventName` で渡す。
 * 利用しない SSE 行は null を返し、壊れた data JSON は通信データ破損として例外にする。
 */
const SSE_DATA_LINE = /^data:\s?(.*)$/;
const SSE_EVENT_LINE = /^event:\s?(.*)$/;
const SSE_CONTROL_LINE = /^(event|id|retry):/;

/** SSE 行から `data:` ペイロード文字列を取り出す。data 行でなければ null。 */
export function extractSSEDataPayload(line: string): string | null {
	const match = SSE_DATA_LINE.exec(line);
	return match ? match[1] : null;
}

/** SSE 行から `event:` 名を取り出す。event 行でなければ null。 */
export function extractSSEEventName(line: string): string | null {
	const match = SSE_EVENT_LINE.exec(line);
	return match ? match[1].trim() : null;
}

export function parseSSEEvent(line: string, eventName?: string | null): SSEFrame | null {
	const name = extractSSEEventName(line);
	if (name === "streamEnd" || name === "reconnect") {
		return { control: name };
	}

	const payload = extractSSEDataPayload(line);
	if (payload === null) {
		if (SSE_CONTROL_LINE.test(line)) return null;
		throw new Error(`SSE line did not match data-record shape: ${JSON.stringify(line)}`);
	}
	if (eventName === "delta") {
		return { delta: JSON.parse(payload) as ChatDeltaDto };
	}
	return JSON.parse(payload) as SSEFrame;
}

/**
 * Split a raw SSE text chunk into individual lines, handling partial chunks.
 * Drops blank lines (record separators) and `:` comment lines (e.g. the
 * backend's `: keepalive` heartbeat).
 */
export function splitSSELines(
	chunk: string,
	buffer: string,
): { lines: string[]; remaining: string } {
	const combined = buffer + chunk;
	const parts = combined.split(/\r\n|\r|\n/);
	const remaining = parts.pop() ?? "";
	const lines = parts.filter((l) => {
		const trimmed = l.trim();
		return trimmed.length > 0 && !trimmed.startsWith(":");
	});
	return { lines, remaining };
}
